<template>
    <section>
        <PageTitle title="Live Orders"
                   v-bind:crumbs="[{href:'/orders',title:'Orders'},{href:'/live_orders',title:'Live Orders'}]"/>

        <div class="wrapper wrapper-content animated fadeInRight">

            <!-- Stat boxes: the glance at what is waiting right now -->
            <div class="row">
                <div class="col-lg-3 col-sm-6">
                    <div class="live-stat" :class="{'live-stat-active': filters.statusId === 7}"
                         @click="filterByStatus(7)">
                        <HorizontalCard title="Un-Assigned" :stat="counts.unassigned"
                                        card-class="yellow-bg" icon="fa fa-exclamation-triangle fa-3x"/>
                    </div>
                </div>
                <div class="col-lg-3 col-sm-6">
                    <div class="live-stat" :class="{'live-stat-active': filters.statusId === 1}"
                         @click="filterByStatus(1)">
                        <HorizontalCard title="Assigned To Branch" :stat="counts.assignedToBranch"
                                        card-class="navy-bg" icon="fa fa-paper-plane fa-3x"/>
                    </div>
                </div>
                <div class="col-lg-3 col-sm-6">
                    <div class="live-stat" :class="{'live-stat-active': filters.statusId === null}"
                         @click="filterByStatus(null)">
                        <HorizontalCard title="Total Live" :stat="counts.liveTotal"
                                        card-class="lazur-bg" icon="fa fa-bolt fa-3x"/>
                    </div>
                </div>
                <div class="col-lg-3 col-sm-6">
                    <HorizontalCard title="Last 60 Minutes" :stat="counts.createdLastHour"
                                    card-class="blue-bg" icon="fa fa-clock-o fa-3x"/>
                </div>
            </div>

            <div class="row">
                <!-- Oldest waiting: the number this page exists to surface -->
                <div class="col-lg-4">
                    <div class="ibox float-e-margins live-panel">
                        <div class="ibox-title">
                            <h5>Oldest Waiting</h5>
                        </div>
                        <div class="ibox-content text-center">
                            <h1 class="no-margins live-age" :class="oldestAgeClass">{{ oldestWaitingLabel }}</h1>
                            <small class="text-muted" v-if="counts.oldestLiveAt">
                                since {{ formatDate(counts.oldestLiveAt) }}
                            </small>
                            <small class="text-muted" v-else>Nothing waiting</small>

                            <div class="progress progress-small m-t-md" v-if="counts.liveTotal > 0">
                                <div class="progress-bar progress-bar-warning"
                                     :style="{width: unassignedPct + '%'}"></div>
                                <div class="progress-bar"
                                     :style="{width: (100 - unassignedPct) + '%'}"></div>
                            </div>
                            <div class="m-t-sm live-legend">
                                <span class="label label-warning">UN-ASSIGNED {{ counts.unassigned }}</span>
                                <span class="label label-success">ASSIGNED {{ counts.assignedToBranch }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Arrivals -->
                <div class="col-lg-8">
                    <div class="ibox float-e-margins live-panel">
                        <div class="ibox-title">
                            <h5>Arrivals &mdash; last 60 minutes</h5>
                            <div class="ibox-tools">
                                <span class="text-muted live-last-event" v-if="lastEventAt">
                                    last event {{ lastEventRelative }}
                                </span>
                            </div>
                        </div>
                        <div class="ibox-content">
                            <LiveArrivalsChart :buckets="arrivalBuckets"/>
                        </div>
                    </div>
                </div>
            </div>

            <!-- The queue -->
            <div class="row">
                <div class="col-lg-12">
                    <div class="ibox float-e-margins">
                        <div class="ibox-title">
                            <h5>
                                Live Queue
                                <span class="label label-primary">{{ totalRows }}</span>
                            </h5>
                            <div class="ibox-tools">
                                <span class="live-conn" :title="connectionLabel">
                                    <span class="live-dot" :class="connectionDotClass"></span>
                                    {{ connectionLabel }}
                                </span>
                                <button type="button" class="btn btn-xs btn-warning m-l-sm"
                                        v-if="!soundReady" @click="enableSound">
                                    <i class="fa fa-volume-up"></i> Enable alert sound
                                </button>
                                <a href="javascript:void(0)" class="m-l-sm" @click="toggleSound"
                                   :title="soundEnabled ? 'Mute new order alert' : 'Unmute new order alert'">
                                    <i class="fa" :class="soundEnabled ? 'fa-volume-up' : 'fa-volume-off'"></i>
                                </a>
                                <a href="javascript:void(0)" class="m-l-sm" @click="reload" title="Refresh now">
                                    <i class="fa fa-refresh" :class="{'fa-spin': loading}"></i>
                                </a>
                            </div>
                        </div>
                        <div class="ibox-content">

                            <!-- Filters -->
                            <div class="row m-b-sm">
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label>Status</label>
                                        <select class="form-control" v-model="statusFilter" @change="applyFilters">
                                            <option :value="null">All live</option>
                                            <option :value="7">UN-ASSIGNED</option>
                                            <option :value="1">ASSIGNED TO BRANCH</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label>Order Id</label>
                                        <input type="text" class="form-control" placeholder="Search by order id"
                                               v-model="orderIdFilter" @keyup.enter="applyFilters"/>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label>Sort</label>
                                        <select class="form-control" v-model="sortOrder" @change="applySort">
                                            <option value="newest">Newest first</option>
                                            <option value="oldest">Longest waiting first</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label>Rows</label>
                                        <select class="form-control" v-model.number="pageSize" @change="applySize">
                                            <option :value="25">25</option>
                                            <option :value="50">50</option>
                                            <option :value="100">100</option>
                                            <option :value="200">200</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="alert alert-info live-new-pill" v-if="newSinceLook > 0"
                                 @click="acknowledgeNew">
                                <i class="fa fa-bell"></i>
                                <strong>{{ newSinceLook }}</strong>
                                new order{{ newSinceLook === 1 ? '' : 's' }} since you last looked.
                                <span class="text-muted">Click to clear.</span>
                            </div>

                            <Loader v-if="loading && rows.length === 0" :default-height="false"/>

                            <!--
                              Not .table-responsive: App.vue sets
                              `@media (min-width:1281px){ .table-responsive{overflow-x:initial!important} }`
                              globally, which disables horizontal scrolling on exactly the
                              desktop widths this table is too wide for.
                            -->
                            <div class="live-table-scroll" v-else>
                                <table class="table table-striped table-bordered table-hover live-table">
                                    <thead>
                                    <tr>
                                        <th>Order ID</th>
                                        <th>Waiting</th>
                                        <th>Status</th>
                                        <th>Restaurant</th>
                                        <th>Branch</th>
                                        <th>Customer</th>
                                        <th>Area</th>
                                        <th>Amount</th>
                                        <th>Created</th>
                                        <th>Type</th>
                                        <th>Source</th>
                                        <th>Payment</th>
                                        <th>Taken By</th>
                                        <th class="text-center"><i class="fa fa-bolt"></i></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="row in rows" :key="row.orderId"
                                        :class="rowClass(row)">
                                        <td class="text-center">
                                            <a href="javascript:void(0)" class="live-order-link"
                                               @click="viewOrder(row.orderId)"
                                               title="Open order detail">{{ row.orderId }}</a>
                                        </td>
                                        <td class="text-center text-nowrap">
                                            <span :class="waitClass(row)">{{ waitingLabel(row) }}</span>
                                        </td>
                                        <td class="text-center">
                                            <span class="label" :class="row.cssClass">{{ row.statusName }}</span>
                                        </td>
                                        <td>{{ row.restaurantName || 'N/A' }}</td>
                                        <td>{{ row.branchName || 'N/A' }}</td>
                                        <td>{{ customerName(row) }}</td>
                                        <td>
                                            {{ deliveryOnly(row, row.areaName) }}
                                            <small class="text-muted live-sub"
                                                   v-if="row.orderTypesId === 3 && row.cityName">
                                                {{ row.cityName }}
                                            </small>
                                        </td>
                                        <td class="text-right text-nowrap">{{ row.billedPrice }}</td>
                                        <td class="text-center text-nowrap">
                                            <span class="label label-default">{{ formatDate(row.orderCreated) }}</span>
                                        </td>
                                        <td class="text-nowrap">{{ row.orderTypeName || 'N/A' }}</td>
                                        <td class="text-nowrap">{{ row.orderSourceName || 'N/A' }}</td>
                                        <td class="text-nowrap">
                                            {{ row.paymentMethodName || 'N/A' }}
                                            <small class="text-muted live-sub" v-if="row.paymentStatusName">
                                                {{ row.paymentStatusName }}
                                            </small>
                                        </td>
                                        <td>{{ row.agentName || 'Baked App Order' }}</td>
                                        <td class="text-center">
                                            <a href="javascript:void(0)" class="btn btn-xs btn-primary"
                                               @click="viewOrder(row.orderId)">View</a>
                                        </td>
                                    </tr>
                                    <tr v-if="rows.length === 0">
                                        <td colspan="14" class="text-center live-empty">
                                            <i class="fa fa-check-circle fa-4x text-navy"></i>
                                            <h3>Nothing waiting</h3>
                                            <p class="text-muted">
                                                Every order has been accepted by a branch or closed.
                                                New orders will appear here the moment they are submitted.
                                            </p>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="row" v-if="totalRows > 0">
                                <div class="col-sm-6">
                                    <small class="text-muted">
                                        Showing {{ rangeFrom }}&ndash;{{ rangeTo }} of {{ totalRows }}
                                    </small>
                                </div>
                                <div class="col-sm-6 text-right">
                                    <div class="btn-group btn-group-sm">
                                        <button type="button" class="btn btn-white"
                                                :disabled="page === 0" @click="goToPage(page - 1)">
                                            <i class="fa fa-angle-left"></i> Prev
                                        </button>
                                        <button type="button" class="btn btn-white" disabled>
                                            Page {{ page + 1 }} of {{ Math.max(totalPages, 1) }}
                                        </button>
                                        <button type="button" class="btn btn-white"
                                                :disabled="page + 1 >= totalPages" @click="goToPage(page + 1)">
                                            Next <i class="fa fa-angle-right"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ViewOrder showActions="true" :orderStatuses="orderStatuses" ref="viewOrderComp"/>
    </section>
</template>

<script>
    /* eslint-disable */
    import PageTitle from '@/components/layout_components/PageTitle'
    import HorizontalCard from '@/components/Dashboard/HorizontalCard'
    import Loader from '@/components/utils/Loader'
    import ViewOrder from '@/components/orders/ViewOrder'
    import LiveArrivalsChart from '@/components/orders/live/LiveArrivalsChart'
    import { unlock, isUnlocked } from '@/services/notificationSound'

    const DELIVERY_ORDER_TYPE = 3

    export default {
        name: "LiveOrders",
        components: {PageTitle, HorizontalCard, Loader, ViewOrder, LiveArrivalsChart},
        data() {
            return {
                orderStatuses: [],
                statusFilter: null,
                orderIdFilter: "",
                sortOrder: "newest",
                pageSize: 25,
                soundReady: isUnlocked(),
                now: Date.now(),
                tickTimer: null
            }
        },
        computed: {
            rows() { return this.$store.state.liveOrders.rows },
            counts() { return this.$store.state.liveOrders.counts },
            filters() { return this.$store.state.liveOrders.filters },
            page() { return this.$store.state.liveOrders.page },
            totalRows() { return this.$store.state.liveOrders.totalRows },
            totalPages() { return this.$store.state.liveOrders.totalPages },
            loading() { return this.$store.state.liveOrders.loading },
            newSinceLook() { return this.$store.state.liveOrders.newSinceLook },
            lastEventAt() { return this.$store.state.liveOrders.lastEventAt },
            soundEnabled() { return this.$store.state.liveOrders.soundEnabled },
            flashIds() { return this.$store.state.liveOrders.flashIds },
            moveIds() { return this.$store.state.liveOrders.moveIds },
            focusOrderId() { return this.$store.state.liveOrders.focusOrderId },
            connectionLabel() { return this.$store.getters['liveOrders/connectionLabel'] },
            unassignedPct() { return this.$store.getters['liveOrders/unassignedPct'] },
            arrivalBuckets() { return this.$store.getters['liveOrders/arrivalBuckets'] },

            connectionDotClass() {
                switch (this.$store.state.liveOrders.connection) {
                    case 'connected': return 'live-dot-ok';
                    case 'disconnected': return 'live-dot-warn';
                    case 'suspended':
                    case 'failed': return 'live-dot-bad';
                    default: return 'live-dot-warn';
                }
            },

            rangeFrom() { return this.totalRows === 0 ? 0 : (this.page * this.pageSize) + 1 },
            rangeTo() { return Math.min((this.page + 1) * this.pageSize, this.totalRows) },

            oldestAgeMinutes() {
                if (!this.counts.oldestLiveAt) return null;
                return (this.now - new Date(this.counts.oldestLiveAt).getTime()) / 60000;
            },
            oldestWaitingLabel() {
                return this.humanizeMinutes(this.oldestAgeMinutes, true);
            },
            oldestAgeClass() {
                const minutes = this.oldestAgeMinutes;
                if (minutes === null) return 'text-muted';
                if (minutes >= 15) return 'text-danger';
                if (minutes >= 7) return 'text-warning';
                return 'text-navy';
            },
            lastEventRelative() {
                return this.lastEventAt ? this.$moment(this.lastEventAt).from(this.now) : '';
            }
        },
        created() {
            this.statusFilter = this.filters.statusId;
            this.sortOrder = this.$store.state.liveOrders.sort;
            this.pageSize = this.$store.state.liveOrders.size;

            this.$http.get(this.$store.state.mainAppURL + "statuses/get_all_statuses").then(resp => {
                this.orderStatuses = resp.data;
            }).catch(() => {});

            this.$store.dispatch('liveOrders/refresh');
        },
        mounted() {
            this.$store.commit('liveOrders/setLivePageMounted', true);
            this.markVisible();
            document.addEventListener('visibilitychange', this.markVisible);

            // The first click anywhere on this page arms the alert sound for later, when the
            // operator has navigated away and actually needs interrupting.
            document.addEventListener('click', this.armSound, {once: true});

            // Local clock only, for the age labels. Makes no requests.
            this.tickTimer = setInterval(() => { this.now = Date.now() }, 1000);

            this.consumeFocus();
        },
        watch: {
            focusOrderId(value) { if (value) this.consumeFocus() }
        },
        beforeDestroy() {
            document.removeEventListener('visibilitychange', this.markVisible);
            document.removeEventListener('click', this.armSound);
            clearInterval(this.tickTimer);
            this.$store.commit('liveOrders/setLivePageVisible', false);
            this.$store.commit('liveOrders/setLivePageMounted', false);
            this.$store.commit('liveOrders/acknowledgeNew');
        },
        methods: {
            /**
             * Tells the store whether anybody is actually watching. New orders arriving while
             * this is true update the table silently; the moment the tab is backgrounded or
             * the operator leaves, the toast and the sound come back.
             */
            markVisible() {
                this.$store.commit('liveOrders/setLivePageVisible',
                    document.visibilityState === 'visible');
            },
            armSound() {
                unlock().then(ok => { this.soundReady = ok });
            },
            enableSound() {
                this.armSound();
            },
            toggleSound() {
                this.$store.commit('liveOrders/toggleSound');
            },
            reload() {
                // The live_orders response embeds counts, so this is the only call needed.
                this.$store.dispatch('liveOrders/refresh');
            },
            acknowledgeNew() {
                this.$store.commit('liveOrders/acknowledgeNew');
            },
            filterByStatus(statusId) {
                this.statusFilter = statusId;
                this.applyFilters();
            },
            applyFilters() {
                const parsed = parseInt(this.orderIdFilter, 10);
                this.$store.commit('liveOrders/setFilters', {
                    statusId: this.statusFilter,
                    orderId: isNaN(parsed) ? null : parsed
                });
                this.$store.dispatch('liveOrders/refresh');
            },
            applySort() {
                this.$store.commit('liveOrders/setSort', this.sortOrder);
                this.$store.dispatch('liveOrders/refresh');
            },
            applySize() {
                this.$store.commit('liveOrders/setSize', this.pageSize);
                this.$store.dispatch('liveOrders/refresh');
            },
            goToPage(page) {
                if (page < 0 || page >= this.totalPages) return;
                this.$store.commit('liveOrders/setPage', page);
                this.$store.dispatch('liveOrders/refresh');
            },
            /**
             * Came in from a toast click. Queue the highlight rather than filtering to that
             * one order: the row is usually still in flight, and narrowing the queue would
             * hide everything else the operator needs to see.
             */
            consumeFocus() {
                const orderId = this.focusOrderId;
                if (!orderId) return;
                this.$store.commit('liveOrders/setFocusOrderId', null);
                this.$store.dispatch('liveOrders/requestFlash', orderId);
            },
            viewOrder(orderId) {
                this.$refs.viewOrderComp.fetchOrderDetail(orderId);
            },
            customerName(row) {
                return row.baykedCustomerName || row.customerName || 'N/A';
            },
            // Area and city are meaningless for anything but a delivery order.
            deliveryOnly(row, value) {
                return row.orderTypesId === DELIVERY_ORDER_TYPE ? (value || 'N/A') : 'N/A';
            },
            waitingMinutes(row) {
                if (!row.orderCreated) return 0;
                return (this.now - new Date(row.orderCreated).getTime()) / 60000;
            },
            waitingLabel(row) {
                return this.humanizeMinutes(this.waitingMinutes(row), false);
            },
            /**
             * Ages here span seconds to years — the queue can hold orders abandoned long
             * ago — so the unit has to scale. Hours stop being readable after a day or two.
             */
            humanizeMinutes(minutes, withSeconds) {
                if (minutes === null || minutes === undefined) return '—';
                if (minutes < 1) return withSeconds ? Math.max(0, Math.floor(minutes * 60)) + 's' : '<1m';

                const totalMinutes = Math.floor(minutes);
                if (totalMinutes < 60) {
                    return withSeconds
                        ? totalMinutes + 'm ' + (Math.floor(minutes * 60) % 60) + 's'
                        : totalMinutes + 'm';
                }

                const hours = Math.floor(totalMinutes / 60);
                if (hours < 24) return hours + 'h ' + (totalMinutes % 60) + 'm';

                const days = Math.floor(hours / 24);
                if (days < 365) return days + 'd ' + (hours % 24) + 'h';

                return Math.floor(days / 365) + 'y ' + (days % 365) + 'd';
            },
            waitClass(row) {
                const minutes = this.waitingMinutes(row);
                if (minutes >= 15) return 'label label-danger';
                if (minutes >= 7) return 'label label-warning';
                return 'label label-default';
            },
            rowClass(row) {
                return {
                    'danger': row.complainCount > 0,
                    'live-row-new': this.flashIds.includes(row.orderId),
                    'live-row-move': this.moveIds.includes(row.orderId)
                };
            }
        }
    }
</script>

<style scoped>
    /* Clickable stat tiles double as the status filter. */
    .live-stat { cursor: pointer; border-radius: 3px; transition: box-shadow .15s ease; }
    .live-stat:hover { box-shadow: 0 0 0 2px rgba(26, 179, 148, .35); }
    .live-stat-active { box-shadow: 0 0 0 3px #1ab394; }

    .live-age { font-size: 30px; font-weight: 600; }

    /* Keep the two panels in this row the same height so they line up. */
    .live-panel .ibox-content {
        min-height: 268px;
    }

    /* The widget's 4x glyph crowds the number it is meant to annotate. */
    .live-stat >>> .widget.style1 .fa {
        opacity: .85;
    }
    .live-stat >>> .widget.style1 {
        padding: 15px 20px;
    }
    .live-legend .label { margin-right: 4px; }
    .live-last-event { font-size: 11px; }

    .live-conn { font-size: 11px; color: #9a9da0; text-transform: uppercase; letter-spacing: .4px; }
    .live-dot {
        display: inline-block; width: 9px; height: 9px;
        border-radius: 50%; margin-right: 5px; vertical-align: middle;
    }
    .live-dot-ok { background: #1ab394; box-shadow: 0 0 0 3px rgba(26, 179, 148, .20); }
    .live-dot-warn { background: #f8ac59; animation: liveDotPulse 1.2s ease-in-out infinite; }
    .live-dot-bad { background: #ed5565; }

    @keyframes liveDotPulse { 0%, 100% { opacity: 1 } 50% { opacity: .3 } }

    /*
      animate.css is loaded globally but its `flash` animates opacity, not background,
      so the row highlight needs its own keyframes. Amber for an arrival (matching the
      UN-ASSIGNED label), brand green for a transition.
    */
    @keyframes liveRowNew { from { background-color: rgba(248, 172, 89, .45) } to { background-color: transparent } }
    @keyframes liveRowMove { from { background-color: rgba(26, 179, 148, .40) } to { background-color: transparent } }

    .live-row-new > td { animation: liveRowNew 2.4s ease-out 1; }
    .live-row-move > td { animation: liveRowMove 2.4s ease-out 1; }

    /*
      Own scroll container rather than Bootstrap's .table-responsive, which App.vue
      neutralises above 1281px with an !important override.
    */
    .live-table-scroll {
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
    }

    .live-table {
        margin-bottom: 0;
        font-size: 12px;
    }

    .live-table > thead > tr > th {
        white-space: nowrap;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: .3px;
        color: #9a9da0;
    }

    .live-table > tbody > tr > td {
        vertical-align: middle;
        padding: 7px 8px;
    }

    .text-nowrap { white-space: nowrap; }

    /* Secondary value tucked under its primary (city under area, status under method). */
    .live-sub {
        display: block;
        font-size: 10px;
        line-height: 1.3;
    }

    .live-order-link { font-weight: 600; color: #1ab394; }
    .live-order-link:hover, .live-order-link:focus { color: #18a689; text-decoration: underline; }

    .live-new-pill { cursor: pointer; margin-bottom: 12px; }
    .live-empty { padding: 45px 15px; }
    .live-empty h3 { margin: 14px 0 6px; }
</style>
